// Жизнь проекта и корпуса (встреча 24.10.2026): состояние (пауза, заморозка, закрыт, завершён),
// удаление по двум подписям и согласование корпуса руководителем.

import { useState } from "react";
import { fmtDate, fmtDateTime, localISODate } from "../shared/format";
import { navigate } from "../shared/router";
import { Button, Drawer, Field, Input, Loading, Modal, useAsync, useToast } from "../shared/ui";
import { api, floorName, isStage } from "./ctx";

export const STATE: Record<string, { label: string; cls: string; hint: string }> = {
  active: { label: "в работе", cls: "s-planned", hint: "Работы идут или вот-вот начнутся по плану." },
  paused: { label: "на паузе", cls: "plain", hint: "Договорились, но старт переносится: нет финансирования, не решены вопросы. Когда вернётесь — назначите новую дату старта." },
  frozen: { label: "заморожен", cls: "s-loading", hint: "Уже строили и остановились: неплатежи, разбирательства, ушли с площадки. При возобновлении план сдвинется на время остановки." },
  closed: { label: "закрыт без завершения", cls: "plain", hint: "Больше не вернёмся: достроят другие или проект отменён." },
  done: { label: "завершён", cls: "s-done", hint: "Сдан заказчику." },
};

export function StateTag({ state }: { state?: string | null }) {
  if (!state || state === "active") return null;
  const s = STATE[state];
  return <span className={`tag ${s?.cls || "plain"}`}>{s?.label || state}</span>;
}

/** Плашка над экраном корпуса, когда он не «в работе»: предупреждений нет, отчёты не принимаются. */
export function StateBanner({ b }: { b: any }) {
  const st = b?.effective_state;
  if (!st || st === "active") return null;
  const own = b.state && b.state !== "active";
  return (
    <div className="alert info" style={{ margin: "12px 16px 0" }}>
      <div className="t">Корпус {STATE[st]?.label || st}{own && b.state_since ? ` с ${fmtDate(b.state_since)}` : own ? "" : " — весь проект"}{own && b.state_reason ? `: ${b.state_reason}` : ""}</div>
      <div className="d">Предупреждения не показываются, отчёты смен не принимаются. Возобновить — «Администрирование» → «Состояние».</div>
    </div>
  );
}

/** Сменить состояние проекта или корпуса; при возобновлении — новая дата старта или сдвиг плана. */
export function StateDrawer({ kind, item, onClose, onDone }: { kind: "project" | "building"; item: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const cur = item.state || "active";
  const [state, setState] = useState(cur === "active" ? "paused" : "active");
  const [reason, setReason] = useState("");
  const [start, setStart] = useState("");
  const [shift, setShift] = useState(true);
  const started = kind === "building" ? !!item.started : true;
  const resuming = state === "active" && (cur === "paused" || cur === "frozen");
  const needReason = ["paused", "frozen", "closed"].includes(state);
  const save = async () => {
    try {
      const r = await api.post(`/${kind === "project" ? "projects" : "buildings"}/${item.id}/state`, {
        state, reason: reason.trim() || null, start_date: resuming && start ? start : null, shift_plan: shift,
      });
      toast.ok(`${kind === "project" ? "Проект" : "Корпус"}: ${r.label}${r.note ? ` — ${r.note}` : ""}`);
      onDone();
    } catch (e) { toast.error(e); }
  };
  return (
    <Drawer narrow title={`Состояние: ${item.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={state === cur || (needReason && reason.trim().length < 3)} onClick={save}>Сохранить</Button>
    </>}>
      <div className="stack">
        <p className="small" style={{ margin: 0 }}>Сейчас: <b>{STATE[cur]?.label}</b>{item.state_since ? ` с ${fmtDate(item.state_since)}` : ""}{item.state_reason ? ` — ${item.state_reason}` : ""}</p>
        {Object.entries(STATE).map(([k, s]) => (
          <label key={k} className="check" style={{ alignItems: "flex-start" }}>
            <input type="radio" name="state" checked={state === k} disabled={k === cur} onChange={() => setState(k)} />
            <span><b>{k === "active" && cur !== "active" ? "Возобновить работы" : s.label[0].toUpperCase() + s.label.slice(1)}</b><div className="small muted">{s.hint}</div></span>
          </label>
        ))}
        {needReason && <Field label="Причина" hint="Видна в «Журнале изменений» и на экранах корпуса"><Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: нет финансирования у заказчика" /></Field>}
        {state === "done" && <Field label="Комментарий" hint="Необязательно"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>}
        {resuming && (kind === "building" && !started ? (
          <Field label="Новая дата старта работ" hint="Пусто — план не меняется"><Input type="date" min={localISODate()} value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        ) : (
          <label className="check"><input type="checkbox" checked={shift} onChange={(e) => setShift(e.target.checked)} /> Сдвинуть план на время остановки{item.state_since ? ` (с ${fmtDate(item.state_since)})` : ""}</label>
        ))}
        {kind === "project" && <p className="small muted">Состояние проекта действует на все его корпуса.</p>}
      </div>
    </Drawer>
  );
}

/** Отметить на удаление: удалит руководитель (другой человек), до этого всё остаётся как было. */
export function DeleteRequestModal({ kind, item, onClose, onDone }: { kind: "project" | "building"; item: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Удалить ${kind === "project" ? "проект" : "корпус"} «${item.name}»?`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="danger" disabled={reason.trim().length < 3} onClick={async () => {
        try {
          await api.post(`/${kind === "project" ? "projects" : "buildings"}/${item.id}/deletion`, { reason: reason.trim() });
          toast.ok("Запрос на удаление отправлен руководителю");
          onDone();
        } catch (e) { toast.error(e); }
      }}>Отправить на удаление</Button>
    </>}>
      <p className="small">Удаление — по двум подписям: вы отмечаете с причиной, руководитель подтверждает или отклоняет. До его решения ничего не меняется.{kind === "project" ? " Вместе с проектом удалятся его корпуса." : ""}</p>
      <Field label="Причина"><Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: завели дважды по ошибке" /></Field>
      <p className="small muted">Если работы просто остановились — лучше «Состояние» → «На паузе» или «Заморожен»: данные останутся, корпус не будет мигать красным.</p>
    </Modal>
  );
}

/** «Требует решения»: корпуса на согласование и запросы на удаление. */
export function PendingPanel({ onChanged, projectId }: { onChanged?: () => void; projectId?: string }) {
  const toast = useToast();
  const d = useAsync<any>(() => api.get("/approvals"), []);
  const [open, setOpen] = useState<any | null>(null);
  const [decide, setDecide] = useState<{ r: any; approve: boolean } | null>(null);
  if (!d.data) return d.error ? null : <Loading />;
  const buildings = d.data.buildings.filter((b: any) => !projectId || b.project_id === projectId);
  const deletions = d.data.deletions.filter((r: any) => !projectId || r.project_id === projectId);
  if (!buildings.length && !deletions.length) return null;
  const done = () => { d.reload(); onChanged?.(); };
  return (
    <div className="panel">
      <div className="ph"><h2>Требует решения · {buildings.length + deletions.length}</h2></div>
      <div className="pb stack tight">
        {deletions.map((r: any) => (
          <div key={r.id} className="row between" style={{ flexWrap: "wrap" }}>
            <span><b>Удалить {r.kind === "project" ? "проект" : "корпус"} «{r.name}»</b>{r.kind === "building" ? <span className="muted"> · {r.project}</span> : null}
              <div className="small muted">Причина: {r.reason}. Запросил {r.requested_by || "—"}, {fmtDateTime(r.requested_at)}</div></span>
            <span className="row tight">
              {r.can_decide && <><Button size="small" variant="danger" onClick={() => setDecide({ r, approve: true })}>Удалить</Button><Button size="small" onClick={() => setDecide({ r, approve: false })}>Отклонить</Button></>}
              {r.mine && <Button size="small" variant="ghost" onClick={async () => { try { await api.del(`/deletions/${r.id}`); toast.ok("Запрос отменён"); done(); } catch (e) { toast.error(e); } }}>Отменить запрос</Button>}
              {!r.can_decide && !r.mine && <span className="small muted">ждёт руководителя</span>}
            </span>
          </div>
        ))}
        {buildings.map((b: any) => (
          <div key={b.id} className="row between" style={{ flexWrap: "wrap" }}>
            <span><b>Согласовать корпус «{b.name}»</b><span className="muted"> · {b.project}</span>
              <div className="small muted">Завёл {b.created_by || "—"}, {fmtDateTime(b.created_at)}</div></span>
            <span className="row tight">
              <Button size="small" variant={b.can_approve ? "primary" : undefined} onClick={() => setOpen(b)}>{b.can_approve ? "Проверить и согласовать" : "Посмотреть"}</Button>
              {!b.can_approve && <span className="small muted">{b.mine ? "согласует руководитель" : "ждёт руководителя"}</span>}
            </span>
          </div>
        ))}
        <div className="small muted">Согласованный корпус больше не меняется: этажи, шаблоны и дата старта закреплены. Удаление подтверждает другой человек — не тот, кто его запросил.</div>
      </div>
      {open && <ApproveDrawer b={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); done(); }} />}
      {decide && (
        <Modal title={decide.approve ? `Удалить «${decide.r.name}»?` : `Отклонить удаление «${decide.r.name}»?`} onClose={() => setDecide(null)} actions={<>
          <Button onClick={() => setDecide(null)}>Отмена</Button>
          <Button variant={decide.approve ? "danger" : "primary"} onClick={async () => {
            try {
              await api.post(`/deletions/${decide.r.id}/decision`, { approve: decide.approve });
              toast.ok(decide.approve ? "Удалено" : "Удаление отклонено");
              setDecide(null); done();
            } catch (e) { toast.error(e); }
          }}>{decide.approve ? "Удалить" : "Отклонить"}</Button>
        </>}>
          <p className="small">{decide.approve ? `${decide.r.kind === "project" ? "Проект и его корпуса пропадут" : "Корпус пропадёт"} из списков у всех. Данные остаются в базе — вернуть может разработчик.` : "Всё останется как было; администратор увидит, что удаление отклонено."}</p>
        </Modal>
      )}
    </div>
  );
}

/** Согласование: руководитель смотрит параметры корпуса — этапы, этажи, шаблоны, старт — и согласует. */
function ApproveDrawer({ b, onClose, onDone }: { b: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const info = useAsync<any>(() => api.get(`/buildings/${b.id}`), [b.id]);
  const floors = useAsync<any[]>(() => api.get(`/buildings/${b.id}/floors`), [b.id]);
  const tpls = useAsync<any[]>(() => api.get("/cycle-templates").catch(() => []), []);
  const tname = (id: string | null) => (id ? tpls.data?.find((t) => t.id === id)?.name || "свой шаблон" : null);
  // этажи подряд с одним шаблоном и типом — одной строкой: «2–10 · типовой · Типовой этаж»
  const groups: { from: number; to: number; kind: string; tpl: string }[] = [];
  for (const f of floors.data || []) {
    const tpl = tname(f.template_id) || `как у корпуса${info.data?.default_template_id ? ` (${tname(info.data.default_template_id) || "шаблон корпуса"})` : ""}`;
    const last = groups[groups.length - 1];
    if (last && !isStage(f.number) && !isStage(last.to) && last.kind === f.kind && last.tpl === tpl && f.number === (last.to === -1 ? 1 : last.to + 1)) last.to = f.number;
    else groups.push({ from: f.number, to: f.number, kind: f.kind, tpl });
  }
  const KIND: Record<string, string> = { prep: "этап", foundation: "этап", underground: "подземный", first: "первый", typical: "типовой", tech: "технический", roof: "кровля" };
  return (
    <Drawer title={`Корпус «${b.name}» · ${b.project}`} onClose={onClose} footer={b.can_approve ? <>
      <Button onClick={onClose}>Позже</Button>
      <Button variant="primary" onClick={async () => { try { await api.post(`/buildings/${b.id}/approve`, {}); toast.ok("Корпус согласован"); onDone(); } catch (e) { toast.error(e); } }}>Согласовать</Button>
    </> : <Button onClick={onClose}>Закрыть</Button>}>
      {!info.data || !floors.data ? <Loading error={info.error || floors.error} /> : (
        <div className="stack">
          <div className="small">Старт работ: <b>{fmtDate(info.data.start_date)}</b> · смен в сутки: <b>{info.data.shifts_per_day}</b> · рабочие дни: <b>{(info.data.workdays || []).map((d: number) => ["пн", "вт", "ср", "чт", "пт", "сб", "вс"][d]).join(", ")}</b></div>
          <table className="table">
            <thead><tr><th>Этап / этажи</th><th>Тип</th><th>Шаблон работ</th></tr></thead>
            <tbody>{groups.map((g) => (
              <tr key={g.from}><td>{isStage(g.from) ? floorName(g.from) : g.from === g.to ? `Этаж ${g.from}` : `Этажи ${g.from}–${g.to}`}</td><td>{KIND[g.kind] || g.kind}</td><td className="small">{g.tpl}</td></tr>
            ))}</tbody>
          </table>
          <div className="row tight"><Button size="small" onClick={() => { onClose(); navigate(`/b/${b.id}/schedule`); }}>Открыть график</Button></div>
          <p className="small muted">После согласования этажи, шаблоны и дата старта закрепляются. Ошибку потом исправляют так: руководитель снимает согласование, администратор правит, руководитель согласует снова.</p>
        </div>
      )}
    </Drawer>
  );
}
